import React from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import SmartRecordsController from '../../controllers/SmartRecordsController';
import ShapeSmartRecord from '../../shapes/ShapeSmartRecord';
import type Shape from '../../objects/Shape';
import styles from './smart-records-panel.module.css';
import containerIndicator from '../../assets/icons/ahaRecords/containerIndicator.svg';
import { svgToIcon } from '../../utils/svg';
import SmartRecordTitle from './SmartRecordTitle';
import { type SmartRecordCreatableType } from '../../types';
import { pluralize } from 'inflected';
import { RECORD_ICONS } from '../../utils/record';
import { capitalize } from 'lodash';

const RecordHierarchy: React.FC<{ record: Shape<ShapeSmartRecord> }> = ({
  record,
}) => {
  const controller = useController(SmartRecordsController);
  const parent = controller.actionGetParent(record);
  const fields = controller.actionGetRecordFields(record);

  // If this record is not in a smart shape hierarchy, we don't show the tooltip
  // because all fields are coming from the user selected values
  if (!Object.entries(fields).some(([, value]) => value?.isSmartShape)) {
    return null;
  }

  return (
    <div className={styles.containerIndicator}>
      {svgToIcon(containerIndicator)}
      <aha-tooltip
        placement='top-end'
        type='popover'
        arrow={false}
        style={{ '--Tooltip__tooltip--outer-padding': 'var(--spacer-2) 0' }}
      >
        <div className={styles.hierarchyContainer}>
          <span className={styles.hierarchyPrimaryText}>
            {`This ${record.shape.terminologyName.toLowerCase()} will be created in:`}
          </span>
          {parent && <SmartRecordTitle record={parent} />}
          {Object.keys(fields).map(key => {
            const fieldType = pluralize(key) as SmartRecordCreatableType;
            if (!fields[key] || fieldType === parent?.recordType) {
              return null;
            }

            return (
              <div className={styles.hierarcyField} key={key}>
                <i
                  style={{
                    color: 'white',
                    backgroundColor: 'var(--aha-gray-800)',
                  }}
                  className={`record-reference-pill__icon ${
                    RECORD_ICONS[capitalize(key)]
                  }`}
                />
                <span className={styles.hierarchyPrimaryText}>
                  {`${ShapeSmartRecord.terminologyFor(fieldType)}`}{' '}
                </span>
                <span className={styles.hierarchySecondaryText}>
                  {fields[key].name}
                </span>
              </div>
            );
          })}
        </div>
      </aha-tooltip>
    </div>
  );
};

export default ApplicationView(RecordHierarchy);
